import { useEffect, useState } from "react";
import type { Conversation, Message } from "@swift/shared";
import { useStore } from "../lib/store";
import { api } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import { scrollToMessage } from "../lib/scrollToMessage";
import { IconPin } from "./icons";

/**
 * Uitklapbare strook direct onder de ConversationHeader die de gepinde
 * berichten toont. Alleen actief op kanalen — voor DM's helemaal niet
 * gerenderd. Klik op een item → spring naar het origineel in de stream
 * (laadt zo nodig oudere pagina's; spiegelt scrollToReplied).
 *
 * De gepinde lijst komt uit de store (gevuld via socket-events
 * pin:added/removed) en wordt bij eerste open van een kanaal opgehaald
 * via api.listPinned(). Daarna is 't gratis.
 */
export default function PinnedStrip({ conversation }: { conversation: Conversation }) {
  const pinned = useStore((s) => s.pinnedByConv[conversation.id] ?? EMPTY);
  const users = useStore((s) => s.users);
  const myUsername = useStore((s) => s.me?.username);
  const setPinned = useStore((s) => s.setPinned);
  const [expanded, setExpanded] = useState(false);

  // Bij eerste opening van 't kanaal de pinned-lijst eenmaal laden.
  // Daarna actualiseert socket 'm.
  useEffect(() => {
    if (conversation.kind !== "channel") return;
    if (useStore.getState().pinnedByConv[conversation.id]) return;
    void api.listPinned(conversation.id).then((r) => {
      setPinned(conversation.id, r.messages);
    });
  }, [conversation.id, conversation.kind, setPinned]);

  if (conversation.kind !== "channel" || pinned.length === 0) return null;

  async function jumpTo(targetId: number) {
    await scrollToMessage({
      conversationId: conversation.id,
      messageId: targetId,
    });
  }

  return (
    <div className="shrink-0 border-b border-slate-700 bg-slate-800/40 text-xs">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 px-4 py-1.5 text-left text-slate-400 hover:bg-slate-800"
      >
        <IconPin size={14} className="shrink-0 text-amber-400" />
        <span className="flex-1">
          {pinned.length} vastgepind bericht{pinned.length === 1 ? "" : "en"}
        </span>
        <span className="text-slate-500">{expanded ? "▴" : "▾"}</span>
      </button>
      {expanded && (
        <ul className="max-h-56 overflow-y-auto border-t border-slate-700">
          {pinned.map((m) => (
            <PinnedItem
              key={m.id}
              message={m}
              senderName={users[m.sender_id]?.display_name ?? `Gebruiker #${m.sender_id}`}
              myUsername={myUsername}
              onJump={() => void jumpTo(m.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

const EMPTY: Message[] = [];

function PinnedItem({
  message,
  senderName,
  myUsername,
  onJump,
}: {
  message: Message;
  senderName: string;
  myUsername?: string;
  onJump(): void;
}) {
  const time = new Date(message.created_at).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <li className="border-b border-slate-700/60 last:border-b-0">
      <button
        onClick={onJump}
        className="flex w-full items-start gap-2 px-4 py-1.5 text-left hover:bg-slate-800"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm text-slate-200">{senderName}</span>
            <span className="shrink-0 text-[10px] text-slate-500">{time}</span>
          </div>
          {message.deleted_at !== null ? (
            <div className="truncate italic text-slate-500">[bericht verwijderd]</div>
          ) : (
            <div
              className="line-clamp-2 text-slate-400"
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(message.body, { highlightSelf: myUsername }),
              }}
            />
          )}
          {message.attachments.length > 0 && (
            <div className="mt-0.5 text-[10px] text-slate-500">
              📎 {message.attachments.length} bijlage
              {message.attachments.length === 1 ? "" : "n"}
            </div>
          )}
        </div>
      </button>
    </li>
  );
}
